import { useGetDevice, useGetDocuments } from "@hooks";
import { Document, DocumentVersion } from "@models";
import { DocStatus } from "@types";
import { ReactNode } from "react";
import { useAnswerQuestionsAction } from "src/components/DocViewer/components/docViewerTopBar/actions/useAnswerQuestionsAction";
import { useGetDocumentsTemplates } from "src/hooks/queries";

type UseTiptapUpdateAnswersParams = {
  document?: Document | null;
  selectedVersion?: DocumentVersion;
  orgId: string;
  deviceId: string;
  docVersionStatus: DocStatus;
  enabled?: boolean;
};

type UseTiptapUpdateAnswersResult = {
  canUpdateAnswers: boolean;
  isPending: boolean;
  onUpdateAnswersClick: () => void;
  confirmationModal: ReactNode;
};

/**
 * Gates and wires the editor's "Update Answers" action, which sends the user
 * back to the MultiStepForm (Template Assistant) to re-answer the version's
 * wizard questions. Availability follows the version's data keys, since those
 * are what the wizard builds its steps from.
 */
export const useTiptapUpdateAnswers = ({
  document,
  selectedVersion,
  orgId,
  deviceId,
  docVersionStatus,
  enabled = true,
}: UseTiptapUpdateAnswersParams): UseTiptapUpdateAnswersResult => {
  const deviceQueriesEnabled = enabled && Boolean(deviceId);
  const { data: device } = useGetDevice({ orgId, deviceId });
  const { data: documents } = useGetDocuments(
    orgId,
    document?.deviceId || deviceId,
    { enabled: deviceQueriesEnabled }
  );
  const { data: templatesConfig } = useGetDocumentsTemplates({
    orgId,
    deviceId,
    enabled: deviceQueriesEnabled,
  });

  const templateConfig = document?.templateKey
    ? templatesConfig?.[document.templateKey]
    : undefined;

  const { confirmationModal, isPending, onAnswerQuestionsClick } =
    useAnswerQuestionsAction({
      selectedDoc: document ?? undefined,
      selectedVersion,
      device,
      documents,
      templateConfig,
      docVersionStatus,
    });

  const canUpdateAnswers = Boolean(
    deviceQueriesEnabled &&
      docVersionStatus === "DRAFT" &&
      document &&
      selectedVersion &&
      device &&
      documents &&
      // The wizard builds its steps from the version's data keys, not the template's,
      // so custom documents without a template can be answered too
      (selectedVersion.dataKeys?.length ?? 0) > 0
  );

  return {
    canUpdateAnswers,
    isPending,
    onUpdateAnswersClick: onAnswerQuestionsClick,
    confirmationModal,
  };
};

export default useTiptapUpdateAnswers;
